import { useInfiniteQuery } from 'react-query';
import { useContext } from 'react';

import { PaginatedApiCollectionResponse } from 'types/api_response';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { ManualSearchResult } from 'views/App/Portal/Policy/_types/search';
import { AppContext } from 'views/App/context';

function toSearchParamsEntries(params: Record<string, unknown>): [string, string][] {
  return Object.entries(params).flatMap(([k, v]) => {
    if (v == null) return [];
    if (Array.isArray(v)) {
      return v.filter((item) => item != null).map((item) => [k, String(item)] as [string, string]);
    }
    return [[k, String(v)] as [string, string]];
  });
}

export default function useIssuedManualSearch<T extends ManualSearchResult>(
  search: string,
  params: Record<string, unknown> = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void,
  organizationId?: string
) {
  const { isFeatureEnabled } = useContext(AppContext);
  const useRUIData = isFeatureEnabled('awbRUIData');
  const url = useRUIData
    ? `${baseApiUrl()}/search/v1/manuals/issued/awb`
    : `${baseApiUrl()}/search/v1/organizations/${organizationId}/manuals/issued/awb`;

  return useInfiniteQuery<PaginatedApiCollectionResponse<T>, Error>(
    ['issued_manual_search_awb', search, params],
    async ({ pageParam, signal }): Promise<PaginatedApiCollectionResponse<T>> => {
      // Return empty results if search is empty
      if (!search) {
        return {
          content: [],
          count: 0,
          nextToken: undefined,
        };
      }

      const entries = toSearchParamsEntries({ ...params, search });
      const paramString = new URLSearchParams(entries).toString();
      const data: PaginatedApiCollectionResponse<T> = await fetcher({
        method: 'get',
        url: pageParam || `${url}?${paramString}`,
        options: { signal },
      });

      return data as PaginatedApiCollectionResponse<T>;
    },
    {
      ...options,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextToken && lastPage.content.length > 0) {
          const entries = toSearchParamsEntries({
            ...params,
            search,
            nextToken: lastPage.nextToken,
          });
          const paramString = new URLSearchParams(entries).toString();
          return `${url}?${paramString}`;
        }
        return undefined;
      },
    }
  );
}
